/**
 * AdminGroupsPage — iter246. Super-admin inventory of ALL groups with
 * bulk archive/unarchive so old test/finished groups can be hidden
 * (data preserved, reversible).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Archive, ArchiveRestore, CheckSquare, Square, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import api from '../lib/api';

export default function AdminGroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('active'); // active | archived

  const load = async () => {
    try {
      const res = await api.get('/admin/groups');
      setGroups(res.data.groups || []);
    } catch {
      toast.error('Could not load groups');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const shown = groups.filter((g) => (tab === 'archived' ? g.archived : !g.archived));
  const allSelected = shown.length > 0 && shown.every((g) => selected.has(g.id));

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const runBulk = async (action) => {
    const ids = shown.filter((g) => selected.has(g.id)).map((g) => g.id);
    if (ids.length === 0) return;
    const verb = action === 'archive' ? 'Archive' : 'Restore';
    if (!window.confirm(`${verb} ${ids.length} group(s)? This hides them from all lists — no data is deleted.`)) return;
    setBusy(true);
    const results = await Promise.allSettled(ids.map((id) => api.post(`/groups/${id}/${action}`)));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    if (ok > 0) toast.success(`${ok} group(s) ${action === 'archive' ? 'archived' : 'restored'}`);
    if (ok < ids.length) toast.error(`${ids.length - ok} failed`);
    setSelected(new Set());
    setBusy(false);
    load();
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-groups-page">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/admin')}
          data-testid="admin-groups-back-btn"
          className="w-9 h-9 -ml-1 rounded-full flex items-center justify-center hover:bg-accent transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold">Groups Archive</h1>
          <p className="text-muted-foreground text-sm">Hide finished or test groups — reversible, nothing is deleted</p>
        </div>
      </div>

      <div className="flex gap-2">
        {[
          { id: 'active', label: `Active (${groups.filter((g) => !g.archived).length})` },
          { id: 'archived', label: `Archived (${groups.filter((g) => g.archived).length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setSelected(new Set()); }}
            data-testid={`groups-tab-${t.id}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
              tab === t.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {shown.length > 0 && (
        <div className="flex items-center justify-between gap-2 px-1" data-testid="groups-bulk-bar">
          <button
            type="button"
            onClick={() => setSelected(allSelected ? new Set() : new Set(shown.map((g) => g.id)))}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="select-all-groups"
          >
            {allSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
            {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
          </button>
          {selected.size > 0 && (
            tab === 'active' ? (
              <Button size="sm" onClick={() => runBulk('archive')} disabled={busy} data-testid="bulk-archive-btn" className="rounded-full text-xs h-8">
                <Archive className="w-3.5 h-3.5 mr-1" /> Archive {selected.size}
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => runBulk('unarchive')} disabled={busy} data-testid="bulk-unarchive-btn" className="rounded-full text-xs h-8">
                <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore {selected.size}
              </Button>
            )
          )}
        </div>
      )}

      <div className="space-y-2">
        {shown.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10" data-testid="no-groups-msg">
            {tab === 'archived' ? 'No archived groups.' : 'No active groups.'}
          </p>
        )}
        {shown.map((g) => (
          <Card
            key={g.id}
            className={`p-3 border-2 rounded-2xl flex items-center gap-3 cursor-pointer transition-colors ${
              selected.has(g.id) ? 'border-primary/60 bg-primary/5' : ''
            }`}
            onClick={() => toggle(g.id)}
            data-testid={`admin-group-row-${g.id}`}
          >
            {selected.has(g.id)
              ? <CheckSquare className="w-5 h-5 text-primary shrink-0" />
              : <Square className="w-5 h-5 text-muted-foreground shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">
                {g.emoji && <span className="mr-1">{g.emoji}</span>}
                {g.name}
                {g.archived && (
                  <span className="ml-2 text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">Archived</span>
                )}
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Users className="w-3 h-3" /> {g.member_count} · {g.group_type || 'group'}
                {g.fund_balance !== 0 && (
                  <span className={g.fund_balance < 0 ? 'text-rose-600 font-semibold' : ''}>
                    {' '}· box ₹{Math.round(g.fund_balance).toLocaleString('en-IN')}
                  </span>
                )}
              </p>
            </div>
          </Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Archived groups disappear from everyone's Home, Groups list, cash boxes and net positions. Restore anytime from the Archived tab.
      </p>
    </div>
  );
}
